import React from 'react';
import { autoUpdate, size, useDismiss, useFloating, useInteractions, useListNavigation, useRole, FloatingFocusManager, FloatingPortal, flip, offset } from '@floating-ui/react';
import { useTranslation } from 'react-i18next';
import * as Styled from './SearchSelect.styled';
import * as InputStyled from './components/SearchInput.styled';
import * as DropdownStyled from './components/Dropdown.styled';
import * as DropdownOptionStyled from './components/DropdownOption.styled';
import { Close } from '../../svgs';
import { LoadingSpinner } from './components/LoadingSpinner';
import { NoResultsMessage } from './components/NoResultsMessage';
import { EllipsisText } from '../../styles/mixins/ellipsisText';

/**
 * SearchSelect options.
 */
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Add unique IDs to each option (without an ID).
 * Important, assumes options will only ever be 1-level deep.
 */
function addOptionComputedIds(options, idPrefix) {
  return options.map((option, index) => {
    const newIdPrefix = `${idPrefix}-${index}`;
    return {
      ...option,
      computedId: newIdPrefix,
      ...(option.subOptions && {
        subOptions: addOptionComputedIds(option.subOptions, newIdPrefix)
      })
    };
  });
}

/**
 * Filter options by search term (matches against the label).
 * Important, assumes options will only ever be 1-level deep.
 */
export function filterOptionsBySearchString(options, searchString = '') {
  const searchLowerCaseString = searchString.toLowerCase();
  return options.map(option => {
    // Filter the subOptions if they exist
    const filteredSubOptions = option.subOptions?.filter(subOption => subOption.label.toLowerCase().includes(searchLowerCaseString));

    // If the main option or any of its subOptions match, retain the option
    if (option.label.toLowerCase().includes(searchLowerCaseString) || filteredSubOptions && filteredSubOptions.length > 0) {
      return {
        ...option,
        // Omit subOptions if there are no children
        ...(filteredSubOptions ? {
          subOptions: filteredSubOptions
        } : undefined)
      };
    }
    return null;
  }).filter(option => option !== null);
}

/**
 * The `SearchSelect` molecule is a combobox.
 *
 * This component leverages `floating-ui` for positioning, focus management, keyboard navigation,
 * and accessibility. It dynamically opens upwards or downwards based on the available space and
 * becomes scrollable when the options exceed the maximum height (about 10 rows).
 *
 * The following **required props** control the component: `value`, `onChange`, `inputValue`, and
 * `onInputChange`.
 *
 * Important! While an input field is provided, **filtering is not handled by the component**. Use
 * the `onInputChange` callback to filter the options by the input field value. A prebuilt filter
 * function is available, `filterOptionsBySearchString`.
 *
 * ```jsx
 * import { SearchSelect, SearchSelectProps, filterOptionsBySearchString } from '@classy/campaign-page-blocks/SearchSelect'
 *
 * const rawOptions = [{ id: '1', label: 'Option 1' }, { id: '2', label: 'Option 2' }]
 * const [options, setOptions] = React.useState(rawOptions)
 * const [value, setValue] = React.useState(options[1])
 * const [inputValue, setInputValue] = React.useState('')
 *
 * React.useEffect(() => {
 *   const filteredOptions = filterOptionsBySearchString(args.options, inputValue)
 *   setOptions(filteredOptions)
 * }, [options, inputValue])
 *
 * return (
 *   <SearchSelect
 *     options={options}
 *     value={value}
 *     onChange={setValue}
 *     inputValue={inputValue}
 *     onValueChange={setInputValue}
 *   />
 * )
 * ```
 */
export const SearchSelect = ({
  options: rawOptions,
  value,
  onChange,
  inputValue,
  onInputChange,
  isLoading,
  noOptionsMessage,
  noResultsMessage,
  renderOption,
  inputProps
}) => {
  const {
    t
  } = useTranslation('blocks.searchSelect');
  const noOptionsString = noOptionsMessage || t('SearchSelect.noOptionsMessage', 'No options to show.');
  const noResultsString = noResultsMessage || t('SearchSelect.noResultsMessage', 'No results were found.');
  const componentId = React.useId();
  const [open, setOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(null);
  const listRef = React.useRef([]);
  const options = React.useMemo(() => addOptionComputedIds(rawOptions, componentId), [componentId, rawOptions]);

  /**
   * Flatten the options by removing group labels and only keeping the children.
   * New flattened array will be used as a lookup to match the selected option.
   */
  const optionsLookup = React.useMemo(() => options.flatMap(option => option.subOptions ? option.subOptions.flat() : option), [options]);
  const {
    refs,
    floatingStyles,
    context
  } = useFloating({
    whileElementsMounted: autoUpdate,
    open,
    onOpenChange: setOpen,
    middleware: [offset(8),
    // 8px offset from the input (it could be above or below)
    flip(), size({
      apply({
        rects,
        availableHeight,
        elements
      }) {
        let maxHeight;
        if (!isLoading && listRef.current.length > 0 && listRef.current[0] !== null) {
          const style = window.getComputedStyle(listRef.current[0]);
          // box-sizing is border-box, so style.height includes padding (and includes "px")
          maxHeight = `min(${availableHeight}px, calc(${style.height} * 10))`;
        }
        Object.assign(elements.floating.style, {
          // Keep dropdown width in sync with input width
          width: `${rects.reference.width}px`,
          maxHeight
        });
      }
    })]
  });
  const role = useRole(context, {
    role: 'listbox'
  });
  const dismiss = useDismiss(context);
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    virtual: true // wires up aria-activedescendant
  });
  const {
    getReferenceProps,
    getFloatingProps,
    getItemProps
  } = useInteractions([role, dismiss, listNav]);
  const hasOptions = React.useMemo(() => !isLoading && optionsLookup.length > 0, [isLoading, optionsLookup.length]);
  const hasGroups = React.useMemo(() => hasOptions && 'subOptions' in options[0], [hasOptions, options]);

  /**
   * Counter for tracking the index of options that are not group labels.
   * The indexes are expected to match the `optionsLookup` array. If we didn't have to support
   * grouped lists, we could get rid of this variable and simply use the map function's index.
   */
  let optionIndexCounter = -1;
  const renderDropdownOption = (option, optionIndex) => {
    const optionProps = getItemProps({
      ref(node) {
        listRef.current[optionIndex] = node;
      },
      onClick() {
        onInputChange(option.label);
        onChange(option);
        setOpen(false);
        refs.domReference.current?.focus();
      }
    });
    const isOptionActive = activeIndex === optionIndex;
    const isSelectedOption = value?.id === option.id;
    return /*#__PURE__*/_jsx(DropdownOptionStyled.DropdownOptionContainer, {
      ...optionProps,
      role: "option",
      id: option.computedId,
      "aria-selected": isOptionActive,
      isOptionActive: isOptionActive,
      isSelectedOption: isSelectedOption,
      isFirstOptionActive: !hasGroups ? optionIndex === 0 : undefined,
      isLastOptionActive: optionIndex === optionsLookup.length - 1,
      "data-id": "cp-search-select-dropdown-item",
      children: renderOption ? renderOption(option, isSelectedOption, isOptionActive) : /*#__PURE__*/_jsx(EllipsisText, {
        children: option.label
      })
    }, option.computedId);
  };
  const renderDropdownGroup = (group, groupIndex) => /*#__PURE__*/_jsxs(DropdownStyled.Group, {
    role: "group",
    "aria-labelledby": group.computedId,
    children: [group.label && /*#__PURE__*/_jsx(DropdownStyled.GroupLabel, {
      id: group.computedId,
      role: "presentation",
      isFirstGroupOptionLabel: groupIndex === 0,
      children: group.label
    }), group.subOptions?.map(option => {
      optionIndexCounter += 1;
      return renderDropdownOption(option, optionIndexCounter);
    })]
  }, group.computedId);
  return /*#__PURE__*/_jsxs(Styled.SearchSelectContainer, {
    children: [/*#__PURE__*/_jsxs(InputStyled.InputWrapper, {
      children: [/*#__PURE__*/_jsx(InputStyled.Input, {
        ...getReferenceProps({
          ref: refs.setReference,
          value: inputValue,
          'aria-autocomplete': 'list',
          onChange(event) {
            const {
              value: newValue
            } = event.target;
            onInputChange(newValue);
            if (newValue) {
              setOpen(true);
            }
          },
          onKeyDown(event) {
            if (event.key !== 'Enter' || event.key === 'Enter' && isLoading) return;

            // Prevent the native onChange event, which will clobber the state setters
            event.preventDefault();
            if (activeIndex != null && optionsLookup[activeIndex]) {
              onInputChange(optionsLookup[activeIndex].label);
              onChange(optionsLookup[activeIndex]);
              setActiveIndex(null);
              setOpen(false);
            }
          },
          onFocus(event) {
            // Select all text when input is focused
            if (event.target.value) {
              event.target.select();
            }

            /**
             * Open the dropdown when the input is focused.
             * Checking if activeIndex is null prevents the dropdown from re-opening
             * after a selection is made.
             */
            if (activeIndex === null) setOpen(true);
          },
          onClick() {
            /**
             * Open the dropdown when the input is clicked.
             * Allows the user to re-open the dropdown when there is a selected option.
             */
            if (activeIndex === null) setOpen(true);
          }
        }),
        "data-id": "cp-search-input",
        ...inputProps
      }), /*#__PURE__*/_jsxs(InputStyled.Icons, {
        children: [value && /*#__PURE__*/_jsx(InputStyled.ClearButton, {
          "aria-label": t('SearchSelect.clearSelection', 'Clear selection'),
          onClick: () => {
            onInputChange('');
            setActiveIndex(null);
            onChange(null);
            refs.domReference.current?.focus();
          },
          children: /*#__PURE__*/_jsx(Close, {})
        }), /*#__PURE__*/_jsx(InputStyled.ArrowDropDownIcon, {
          "aria-hidden": "true",
          onClick: () => setOpen(true)
        })]
      })]
    }), open && /*#__PURE__*/_jsx(FloatingPortal, {
      children: /*#__PURE__*/_jsx(FloatingFocusManager, {
        context: context,
        initialFocus: -1,
        children: /*#__PURE__*/_jsxs(DropdownStyled.Dropdown, {
          ...getFloatingProps({
            ref: refs.setFloating,
            style: floatingStyles
          }),
          children: [isLoading && /*#__PURE__*/_jsx(LoadingSpinner, {}), !isLoading && inputValue === '' && rawOptions.length === 0 && /*#__PURE__*/_jsx(NoResultsMessage, {
            children: noOptionsString
          }), !isLoading && inputValue !== '' && !hasOptions && /*#__PURE__*/_jsx(NoResultsMessage, {
            children: noResultsString
          }), hasOptions &&
          /*#__PURE__*/
          /**
           * We could have been fancy and used a recursive function to generate the menu,
           * but there will only ever be one level of nesting, so we can keep it simple.
           */
          _jsx(_Fragment, {
            children: hasGroups ? options.map(renderDropdownGroup) : /*#__PURE__*/_jsx(DropdownStyled.Group, {
              role: "group",
              children: options.map(option => {
                optionIndexCounter += 1;
                return renderDropdownOption(option, optionIndexCounter);
              })
            })
          })]
        })
      })
    })]
  });
};